import React, { useRef, useMemo, useEffect } from 'react'
import * as yup from 'yup'

import { Form, Button, TextInput, FormButtons, DatePicker } from '@/components'
import CustomerSelector from '@/pages/Customer/customerSelector'
import { IDriver } from '@/types'
import { useForm } from '@/hooks'
import { print } from '@/utils'
import { t } from '@/i18n'

interface Props {
  details: any[]
  loading: boolean
  isEditing?: boolean
  submitting: boolean
  onCancel: () => void
  initialData?: Partial<IDriver>
  onSubmit: (d: IDriver) => void
}

const EditDriverForm: React.FC<Props> = ({
  loading,
  details,
  onCancel,
  onSubmit,
  isEditing,
  submitting,
  initialData,
}) => {
  const schema = useMemo(
    () =>
      yup
        .object({
          name: yup.string().trim().required(t('required_field_error')),
          customer: yup.string().required(t('required_field_error')),
          email: yup
            .string()
            .trim()
            .email()
            .required(t('invalid_format_error')),
        })
        .required(),
    [],
  )

  const { control, isValid, getValues, onTriggerChange, onFormSubmit } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  const formRef = useRef<HTMLFormElement | null>(null)

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  useEffect(() => {
    if (initialData?.customer) {
      onTriggerChange('customer', initialData.customer.id)
    }
  }, [initialData?.customer, onTriggerChange])

  return (
    <>
      <div className="main-info">
        <div className="section-box">
          <div className="toolbox">
            <div className="title-box">
              <h4>{t('edit_driver')}</h4>
            </div>
          </div>

          <div className="edit-data">
            <Form<IDriver>
              formRef={formRef}
              control={control}
              isEditing={isEditing}
              onSubmit={onFormSubmit}
            >
              <div className="form-fields">
                {details.map(field => {
                  if (!field.name) return null
                  return (
                    <TextInput
                      key={field.name}
                      name={field.name}
                      control={control}
                      label={t(field.label)}
                      emptyValue={emptyValue}
                      className={field.className}
                      disabled={!Boolean(isEditing)}
                    />
                  )
                })}
                <DatePicker
                  name="birthday"
                  className="w-50"
                  control={control}
                  label={t('birthday')}
                  emptyValue={emptyValue}
                  dateFormat="yyyy-MM-dd"
                  disabled={!Boolean(isEditing)}
                />
                <CustomerSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  isEditing={isEditing}
                  initial={initialData?.customer}
                  selected={getValues('customer')}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
      <FormButtons
        formRef={formRef}
        visible={isEditing}
        onCancel={onCancel}
        isFormValid={isValid}
        submitting={submitting}
        cancelClassName={'gray'}
      />
    </>
  )
}

export default EditDriverForm
